import React, { useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlertTriangle, ChevronDown, ChevronRight, RefreshCw, Upload, Settings } from "lucide-react";
import api from "../api";

const fetchReplenishment = (days) =>
  api.get("/inventory/replenishment-by-city", { params: { days } }).then(r => r.data);
const fetchCities = () => api.get("/fba-cities").then(r => r.data);
const fetchUnmapped = () => api.get("/fba-cities/unmapped").then(r => r.data);

function StockDays({ days }) {
  if (days >= 999) return <span style={{ color: "#38a169" }}>∞</span>;
  const color = days < 14 ? "#e53e3e" : days < 30 ? "#dd6b20" : days < 60 ? "#d69e2e" : "#38a169";
  return <span style={{ color, fontWeight: 700 }}>{days}d</span>;
}

function ConfidenceDot({ confidence }) {
  const color = confidence === "high" ? "#38a169" : confidence === "medium" ? "#dd6b20" : "#aaa";
  const label = confidence === "high" ? "High accuracy" : confidence === "medium" ? "Medium" : "Improving daily";
  return <span title={label} style={{ color, marginLeft: "4px", fontSize: "0.7rem" }}>●</span>;
}

function CityManager({ onClose }) {
  const qc = useQueryClient();
  const { data: cities } = useQuery({ queryKey: ["fba-cities"], queryFn: fetchCities });
  const { data: unmapped } = useQuery({ queryKey: ["fba-unmapped"], queryFn: fetchUnmapped });
  const [newCity, setNewCity] = useState("");
  const [newFC, setNewFC] = useState("");
  const [customFC, setCustomFC] = useState("");

  const save = async () => {
    const fc = newFC === "__custom__" ? customFC.toUpperCase() : newFC;
    if (!newCity || !fc) return;
    await api.post("/fba-cities", { city_name: newCity, fulfillment_center: fc });
    qc.invalidateQueries(["fba-cities"]);
    qc.invalidateQueries(["fba-unmapped"]);
    setNewFC(""); setCustomFC("");
  };

  const remove = async (fc) => {
    await api.delete(`/fba-cities/${fc}`);
    qc.invalidateQueries(["fba-cities"]);
  };

  return (
    <div style={{ position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
      background: "rgba(0,0,0,0.5)", zIndex: 1000, display: "flex",
      alignItems: "center", justifyContent: "center" }}>
      <div style={{ background: "white", borderRadius: "12px", padding: "1.5rem",
        width: "600px", maxHeight: "80vh", overflow: "auto" }}>
        <div style={{ display: "flex", justifyContent: "space-between", marginBottom: "1rem" }}>
          <h3 style={{ fontWeight: 700 }}>Manage FBA City Groups</h3>
          <button onClick={onClose} style={{ background: "none", border: "none", cursor: "pointer", fontSize: "1.2rem" }}>✕</button>
        </div>

        {/* Add new mapping */}
        <div style={{ background: "#f8f9ff", padding: "12px", borderRadius: "8px", marginBottom: "1rem" }}>
          <div style={{ fontWeight: 600, marginBottom: "8px", fontSize: "0.875rem" }}>Add Center to City</div>
          <div style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}>
            <input value={newCity} onChange={e => setNewCity(e.target.value)}
              placeholder="City name (e.g. Bangalore)"
              style={{ flex: 1, padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }} />
            <select value={newFC} onChange={e => setNewFC(e.target.value)}
              style={{ flex: 1, padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }}>
              <option value="">-- Select Center --</option>
              {unmapped?.map(fc => <option key={fc} value={fc}>{fc}</option>)}
              <option value="__custom__">Enter manually...</option>
            </select>
            {newFC === "__custom__" && (
              <input value={customFC} onChange={e => setCustomFC(e.target.value)}
                placeholder="FC code e.g. HYD9"
                style={{ width: "120px", padding: "7px 10px", border: "1px solid #e0e0e0", borderRadius: "6px", fontSize: "0.875rem" }} />
            )}
            <button className="btn btn-primary btn-sm" onClick={save}>Add</button>
          </div>
        </div>

        {/* Current mappings */}
        {cities?.map(city => (
          <div key={city.city} style={{ marginBottom: "12px", padding: "10px 12px",
            border: "1px solid #e0e0e0", borderRadius: "8px" }}>
            <div style={{ fontWeight: 700, marginBottom: "6px" }}>🏙️ {city.city}</div>
            <div style={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
              {city.centers.map(fc => (
                <span key={fc} style={{ background: "#f0f4ff", padding: "3px 8px",
                  borderRadius: "4px", fontSize: "0.8rem", display: "flex", alignItems: "center", gap: "4px" }}>
                  {fc}
                  <button onClick={() => remove(fc)}
                    style={{ background: "none", border: "none", cursor: "pointer",
                      color: "#e53e3e", fontSize: "0.75rem", padding: 0 }}>✕</button>
                </span>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

export default function Replenishment() {
  const [days, setDays] = useState(60);
  const [expanded, setExpanded] = useState({});
  const [uploading, setUploading] = useState(false);
  const [uploadMsg, setUploadMsg] = useState("");
  const [showCityManager, setShowCityManager] = useState(false);

  const { data, isLoading, refetch } = useQuery({
    queryKey: ["replenishment-city", days],
    queryFn: () => fetchReplenishment(days)
  });

  const toggle = (name) => setExpanded(e => ({ ...e, [name]: !e[name] }));
  const alerts = data?.filter(r => r.replenish_alert) || [];

  const handleUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    setUploadMsg("");
    const formData = new FormData();
    formData.append("file", file);
    try {
      const token = localStorage.getItem("dash_token");
      const res = await fetch("/api/inventory/upload-opening-balance", {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: formData
      });
      const data = await res.json();
      setUploadMsg(data.message);
      refetch();
    } catch { setUploadMsg("Upload failed"); }
    setUploading(false);
  };

  return (
    <div className="page">
      {showCityManager && <CityManager onClose={() => setShowCityManager(false)} />}

      <div className="page-header">
        <div>
          <div className="page-title">FBA Replenishment</div>
          <div className="page-subtitle">Stock and velocity grouped by city</div>
        </div>
        <div style={{ display: "flex", gap: "8px", alignItems: "center", flexWrap: "wrap" }}>
          <select value={days} onChange={e => setDays(Number(e.target.value))}
            style={{ padding: "7px 12px", border: "1px solid #e0e0e0", borderRadius: "8px", fontSize: "0.875rem" }}>
            <option value={30}>Last 30 days</option>
            <option value={60}>Last 60 days</option>
            <option value={90}>Last 90 days</option>
          </select>
          <button className="btn btn-outline btn-sm" onClick={() => refetch()}>
            <RefreshCw size={14} /> Refresh
          </button>
          <button className="btn btn-outline btn-sm" onClick={() => setShowCityManager(true)}>
            <Settings size={14} /> Manage Cities
          </button>
          <label className="btn btn-outline btn-sm" style={{ cursor: "pointer" }}>
            <Upload size={14} /> {uploading ? "Uploading..." : "Update Stock Base"}
            <input type="file" accept=".txt,.csv" onChange={handleUpload} style={{ display: "none" }} />
          </label>
        </div>
      </div>

      {uploadMsg && (
        <div style={{ background: "#f0fff4", border: "1px solid #9ae6b4", borderRadius: "8px",
          padding: "10px 16px", marginBottom: "1rem", color: "#276749", fontSize: "0.875rem" }}>
          ✅ {uploadMsg}
        </div>
      )}

      {alerts.length > 0 && (
        <div className="alert alert-warning" style={{ display: "flex", gap: "10px", alignItems: "flex-start", marginBottom: "1.5rem" }}>
          <AlertTriangle size={18} style={{ flexShrink: 0, marginTop: 1 }} />
          <div>
            <strong>⚠️ {alerts.length} product{alerts.length > 1 ? "s" : ""} need replenishment:</strong>
            {alerts.map(a => (
              <div key={a.product_name} style={{ marginTop: "4px", fontSize: "0.85rem" }}>
                <strong>{a.product_name}</strong> — {a.days_of_stock}d left, send <strong>{a.refill_needed} units</strong>
              </div>
            ))}
          </div>
        </div>
      )}

      {isLoading ? <div className="loading">Loading replenishment data...</div> : (
        <div className="table-card">
          <div className="table-header">
            <span style={{ fontWeight: 600 }}>All Products — Click to expand cities</span>
            <span style={{ fontSize: "0.75rem", color: "#888" }}>
              <span style={{ color: "#38a169" }}>●</span> High &nbsp;
              <span style={{ color: "#dd6b20" }}>●</span> Medium &nbsp;
              <span style={{ color: "#aaa" }}>●</span> Improving accuracy
            </span>
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.875rem" }}>
            <thead>
              <tr style={{ background: "#fafafa" }}>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>PRODUCT</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>TOTAL FBA STOCK</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>SOLD ({days}D)</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>DAILY RATE</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>DAYS OF STOCK</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>REFILL (2 MONTHS)</th>
                <th style={{ textAlign: "left", padding: "10px 16px", color: "#888", fontSize: "0.75rem" }}>STATUS</th>
              </tr>
            </thead>
            <tbody>
              {data?.map(product => (
                <React.Fragment key={product.product_name}>
                  <tr onClick={() => toggle(product.product_name)}
                    style={{ cursor: "pointer", background: expanded[product.product_name] ? "#f0f4ff" : "white",
                      borderBottom: "1px solid #f0f0f0" }}>
                    <td style={{ padding: "12px 16px", fontWeight: 700 }}>
                      <span style={{ marginRight: "8px" }}>
                        {expanded[product.product_name] ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                      </span>
                      📦 {product.product_name}
                    </td>
                    <td style={{ padding: "12px 16px", fontWeight: 600 }}>{(product.total_fba_stock || 0).toLocaleString()}</td>
                    <td style={{ padding: "12px 16px" }}>{(product.total_sold_60d || 0).toLocaleString()}</td>
                    <td style={{ padding: "12px 16px" }}>{product.overall_daily_rate}/day</td>
                    <td style={{ padding: "12px 16px" }}><StockDays days={product.days_of_stock} /></td>
                    <td style={{ padding: "12px 16px", fontWeight: 700,
                      color: product.refill_needed > 0 ? "#e53e3e" : "#38a169" }}>
                      {product.refill_needed > 0 ? `${product.refill_needed} units` : "✓ Stocked"}
                    </td>
                    <td style={{ padding: "12px 16px" }}>
                      {product.days_of_stock < 14 ? <span className="badge red">🚨 Urgent</span>
                        : product.days_of_stock < 30 ? <span className="badge orange">⚠️ Low</span>
                        : product.days_of_stock < 60 ? <span className="badge orange">Monitor</span>
                        : <span className="badge green">✓ OK</span>}
                    </td>
                  </tr>

                  {expanded[product.product_name] && (
                    <tr style={{ background: "#f8f9ff" }}>
                      <td colSpan={7} style={{ padding: "0 0 12px 48px" }}>
                        <table style={{ width: "95%", borderCollapse: "collapse", fontSize: "0.825rem", marginTop: "8px" }}>
                          <thead>
                            <tr>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>CITY</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>CENTERS</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>SOLD ({days}D)</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>DAILY RATE</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>STOCK TODAY</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>STOCK LASTS TILL</th>
                              <th style={{ textAlign: "left", padding: "6px 12px", color: "#4361ee", fontSize: "0.75rem" }}>REFILL 2 MONTHS</th>
                            </tr>
                          </thead>
                          <tbody>
                            {product.cities?.map(city => {
                              const lastsDate = city.days_of_stock < 999
                                ? new Date(Date.now() + city.days_of_stock * 86400000)
                                  .toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" })
                                : "∞";
                              return (
                                <tr key={city.city} style={{ borderBottom: "1px solid #eee" }}>
                                  <td style={{ padding: "8px 12px", fontWeight: 700 }}>🏙️ {city.city}</td>
                                  <td style={{ padding: "8px 12px", color: "#888", fontSize: "0.75rem" }}>{city.centers_list}</td>
                                  <td style={{ padding: "8px 12px" }}>{city.total_sold_60d}</td>
                                  <td style={{ padding: "8px 12px" }}>{city.daily_rate}/day</td>
                                  <td style={{ padding: "8px 12px", fontWeight: 600,
                                    color: city.total_stock < 10 ? "#e53e3e" : city.total_stock < 30 ? "#dd6b20" : "#38a169" }}>
                                    {city.total_stock}
                                    <ConfidenceDot confidence={city.confidence} />
                                  </td>
                                  <td style={{ padding: "8px 12px" }}>
                                    <StockDays days={city.days_of_stock} />
                                    {city.days_of_stock < 999 && (
                                      <span style={{ color: "#888", fontSize: "0.75rem", marginLeft: "6px" }}>({lastsDate})</span>
                                    )}
                                  </td>
                                  <td style={{ padding: "8px 12px", fontWeight: 700,
                                    color: city.refill_2months > 0 ? "#e53e3e" : "#38a169" }}>
                                    {city.refill_2months > 0 ? `${city.refill_2months} units` : "✓ OK"}
                                  </td>
                                </tr>
                              );
                            })}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
